import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Layout, message as antDMessage } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../contexts/AuthContext';
import useLanguageContext from '../../../contexts/LanguageContext';
import { Notifications } from '../../Notifications/Notifications';
import { getLocalizationMenu, getProfileMenuItems } from './headerMenuUtils';
import HeaderUserAndLanguageDropdowns, {
  StyledDropdownContainer
} from './HeaderDropdowns';
import { useSocketContext } from '../../../contexts/SocketContext';
import { useErrorMessage } from '../../../utils/errorMessage';
import { PAGE_TITLE_SLOT_ID } from './headerConstants';

const { Header } = Layout;

const StyledHeader = styled.div`
  && {
    height: ${(props) => props.$height}px;
    line-height: normal;
    padding: 8px 40px 0;
    ${(props) =>
      props.$siderWidth === undefined ? '' : 'width: auto !important;'}
  }
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${(props) =>
    props.$siderWidth === undefined
      ? `
    position: fixed;
    right: 0;
    width: ${props.width};
  `
      : `
    flex-shrink: 0;
    margin-left: ${props.$siderWidth}px;
    transition: margin-left 0.2s;
  `}

  @media (max-width: 992px) {
    justify-content: space-between;
  }

  @media (max-width: 768px) {
    && {
      padding: 0 12px;
    }
  }
`;

const StyledTitleSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

const TriggerCollapse = styled.div`
  color: var(--textColor);
  font-size: 20px;
  padding: 0 24px;
  cursor: pointer;
  z-index: 10000;
  :hover {
    color: var(--primaryColor);
  }
`;

const HeaderLayout = ({
  collapseSider,
  collapsed,
  collapseWidth,
  themeLogo,
  headerHeight,
  siderWidth
}) => {
  const navigate = useNavigate();
  const { user, dispatchAPI, token } = useAuthContext();
  const { dispatch: dispatchLocale } = useLanguageContext();
  const { t, i18n } = useTranslation();
  const [widthTrigger, setWidthTrigger] = useState('100vw');
  const [notifications, setNotifications] = useState([]);
  const { message } = useErrorMessage();

  const onProfileMenuClick = ({ key }) => {
    if (key === 'logout') {
      dispatchAPI('LOGOUT');
      antDMessage.success(t('login.logoutSuccess'));
    }
  };

  const onLanguageMenuClick = ({ key }) => {
    dispatchLocale({ type: 'SET_LOCALE', locale: key });
  };

  const localizationMenu = getLocalizationMenu(onLanguageMenuClick, i18n);
  const profileMenu = {
    onClick: onProfileMenuClick,
    items: getProfileMenuItems({
      user,
      t,
      navigate,
      profilePath: '/profile'
    })
  };

  const getNotifications = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `notifications?recipients=${user?._id}&populate=item.type`
      });
      setNotifications(data);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      if (token) {
        await getNotifications();
      }
    })();
  }, [user]);

  const { socket } = useSocketContext();

  useEffect(() => {
    if (socket) {
      const handleNewNotification = () => getNotifications();
      const handleDeleteNotification = () => getNotifications();
      const handlePatchNotification = () => getNotifications();

      socket.on('newNotification', handleNewNotification);
      socket.on('deleteNotification', handleDeleteNotification);
      socket.on('patchNotification', handlePatchNotification);

      return () => {
        socket.off('newNotification', handleNewNotification);
        socket.off('deleteNotification', handleDeleteNotification);
        socket.off('patchNotification', handlePatchNotification);
      };
    }
    return undefined;
  }, [socket]);

  useEffect(() => {
    if (collapsed) {
      if (collapseWidth === 0) {
        setWidthTrigger('100vw');
      } else {
        setWidthTrigger('calc(100vw - 80px)');
      }
    } else {
      setWidthTrigger('calc(100vw - 256px)');
    }
  }, [collapsed, collapseWidth]);

  return (
    <StyledHeader
      as={Header}
      width={widthTrigger}
      $height={headerHeight}
      $siderWidth={siderWidth}
    >
      {globalThis.innerWidth < 992 && (
        <TriggerCollapse
          as={collapsed ? MenuUnfoldOutlined : MenuFoldOutlined}
          onClick={() => collapseSider(!collapsed)}
        />
      )}
      <StyledTitleSlot id={PAGE_TITLE_SLOT_ID} />
      {themeLogo && (
        <img
          style={{
            width: 100,
            maxHeight: 50,
            position: 'absolute',
            top: 10,
            left: '50%',
            marginLeft: '-50px'
          }}
          src={themeLogo}
          alt="Company logo"
        />
      )}
      <StyledContainer>
        <StyledDropdownContainer>
          <Notifications notifications={notifications} />
        </StyledDropdownContainer>
        <HeaderUserAndLanguageDropdowns
          profileMenu={profileMenu}
          localizationMenu={localizationMenu}
          user={user}
          t={t}
          StyledDropdownContainerComponent={StyledDropdownContainer}
        />
      </StyledContainer>
    </StyledHeader>
  );
};

HeaderLayout.propTypes = {
  collapseSider: PropTypes.func.isRequired,
  collapsed: PropTypes.bool.isRequired,
  collapseWidth: PropTypes.number.isRequired,
  themeLogo: PropTypes.string,
  headerHeight: PropTypes.number,
  siderWidth: PropTypes.number
};

HeaderLayout.defaultProps = {
  themeLogo: null,
  headerHeight: 64,
  siderWidth: undefined
};

export default HeaderLayout;
